import { forwardRef, useEffect, useRef } from "react";
import type { InputHTMLAttributes } from "react";
import { Icon } from "./Icon.js";
import "./Checkbox.css";

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  label?: string;
  /** Visual "some but not all" state — a real DOM property, not an HTML attribute,
   * so it has to be set imperatively on the node rather than passed as a prop. */
  indeterminate?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, checked, className, id, ...rest },
  ref
) {
  const innerRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label className={["ui-checkbox", className ?? ""].filter(Boolean).join(" ")} htmlFor={id}>
      <span className="ui-checkbox__box-wrap">
        {/* Real checkbox input drives keyboard focus, space-to-toggle, and
            screen-reader state; it's visually hidden and the .ui-checkbox__box
            span beside it is purely decorative (aria-hidden). */}
        <input
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
          }}
          id={id}
          type="checkbox"
          className="ui-checkbox__input"
          checked={checked}
          {...rest}
        />
        <span className="ui-checkbox__box" aria-hidden="true">
          <Icon name="check" size={11} strokeWidth={3.2} className="ui-checkbox__tick" />
        </span>
      </span>
      {label && <span className="ui-checkbox__label">{label}</span>}
    </label>
  );
});
